// Stroke geometry follows signature_pad (velocity-based Bézier width) and
// blends in hardware pressure when the pen actually reports it. A flat 0.5
// pressure is the Pointer Events default for buttons, not a pressure sensor.

export interface InkPoint {
  x: number
  y: number
  pressure: number | null
  time: number
}

export interface InkDot {
  x: number
  y: number
  radius: number
}

export interface InkCurve {
  start: {x: number; y: number}
  control1: {x: number; y: number}
  control2: {x: number; y: number}
  end: {x: number; y: number}
  startWidth: number
  endWidth: number
}

export const INK_MIN_WIDTH = 0.5
export const INK_MAX_WIDTH = 2.5
const VELOCITY_FILTER_WEIGHT = 0.7

export type InkKind = 'pen' | 'pointer'

export function inkWidth(
  velocity: number,
  pressure: number | null,
  minWidth = INK_MIN_WIDTH,
  maxWidth = INK_MAX_WIDTH,
): number {
  const fromVelocity = Math.max(maxWidth / (velocity + 1), minWidth)
  if (pressure == null || Number.isNaN(pressure)) return fromVelocity
  const clamped = pressure < 0 ? 0 : pressure > 1 ? 1 : pressure
  const fromPressure = minWidth + (maxWidth - minWidth) * clamped
  return fromPressure * 0.8 + fromVelocity * 0.2
}

export function usesHardwarePressure(points: InkPoint[]): boolean {
  return points.some(
    point => point.pressure != null && Math.abs(point.pressure - 0.5) > 0.02,
  )
}

export function acceptPoint(
  previous: InkPoint | undefined,
  next: InkPoint,
  kind: InkKind,
): boolean {
  if (!previous) return true
  const minDistance = kind === 'pen' ? 0.75 : 5
  const dx = next.x - previous.x
  const dy = next.y - previous.y
  return dx * dx + dy * dy >= minDistance * minDistance
}

export class SignatureInk {
  strokes: InkPoint[][] = []
  private active: InkPoint[] | null = null
  private activeKind: InkKind = 'pointer'

  get isEmpty(): boolean {
    return (
      this.strokes.length === 0 && (!this.active || this.active.length === 0)
    )
  }

  begin(point: InkPoint, kind: InkKind) {
    if (this.active && this.active.length > 0) this.end()
    this.activeKind = kind
    this.active = [point]
  }

  extend(point: InkPoint) {
    if (!this.active) {
      this.begin(point, point.pressure != null ? 'pen' : 'pointer')
      return
    }
    const previous = this.active[this.active.length - 1]
    if (!acceptPoint(previous, point, this.activeKind)) return
    this.active.push(point)
  }

  end(): boolean {
    if (!this.active || this.active.length === 0) {
      this.active = null
      return false
    }
    this.strokes.push(this.active)
    this.active = null
    return true
  }

  clear() {
    this.strokes = []
    this.active = null
  }

  allStrokes(): InkPoint[][] {
    if (this.active && this.active.length > 0)
      return this.strokes.concat([this.active])
    return this.strokes
  }
}

function controlPoints(
  s1: {x: number; y: number},
  s2: {x: number; y: number},
  s3: {x: number; y: number},
) {
  const dx1 = s1.x - s2.x
  const dy1 = s1.y - s2.y
  const dx2 = s2.x - s3.x
  const dy2 = s2.y - s3.y
  const m1 = {x: (s1.x + s2.x) / 2, y: (s1.y + s2.y) / 2}
  const m2 = {x: (s2.x + s3.x) / 2, y: (s2.y + s3.y) / 2}
  const l1 = Math.hypot(dx1, dy1)
  const l2 = Math.hypot(dx2, dy2)
  const k = l2 / (l1 + l2 || 1)
  const cm = {x: m2.x + (m1.x - m2.x) * k, y: m2.y + (m1.y - m2.y) * k}
  const tx = s2.x - cm.x
  const ty = s2.y - cm.y
  return {
    c1: {x: m1.x + tx, y: m1.y + ty},
    c2: {x: m2.x + tx, y: m2.y + ty},
  }
}

export function buildStrokeGeometry(points: InkPoint[]): {
  dots: InkDot[]
  curves: InkCurve[]
} {
  const dots: InkDot[] = []
  const curves: InkCurve[] = []
  if (points.length === 0) return {dots, curves}

  const hardwarePressure = usesHardwarePressure(points)
  const pressureOf = (point: InkPoint) =>
    hardwarePressure ? point.pressure : null
  const dotRadius = hardwarePressure
    ? inkWidth(0, pressureOf(points[0]))
    : (INK_MIN_WIDTH + INK_MAX_WIDTH) / 2

  if (points.length === 1) {
    dots.push({x: points[0].x, y: points[0].y, radius: dotRadius})
    return {dots, curves}
  }

  dots.push({x: points[0].x, y: points[0].y, radius: dotRadius})

  if (points.length === 2) {
    const dt = points[1].time - points[0].time
    const instant =
      dt > 0
        ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) / dt
        : 0
    curves.push({
      start: points[0],
      control1: points[0],
      control2: points[1],
      end: points[1],
      startWidth: inkWidth(0, pressureOf(points[0])),
      endWidth: inkWidth(instant, pressureOf(points[1])),
    })
    return {dots, curves}
  }

  let lastVelocity = 0
  let lastWidth = (INK_MIN_WIDTH + INK_MAX_WIDTH) / 2
  const buffer: InkPoint[] = []

  points.forEach(point => {
    buffer.push(point)
    if (buffer.length <= 2) return
    if (buffer.length === 3) buffer.unshift(buffer[0])
    const c2 = controlPoints(buffer[0], buffer[1], buffer[2]).c2
    const c3 = controlPoints(buffer[1], buffer[2], buffer[3]).c1
    const start = buffer[1]
    const end = buffer[2]
    const dt = end.time - start.time
    const instant =
      dt > 0 ? Math.hypot(end.x - start.x, end.y - start.y) / dt : 0
    const velocity =
      VELOCITY_FILTER_WEIGHT * instant +
      (1 - VELOCITY_FILTER_WEIGHT) * lastVelocity
    const newWidth = inkWidth(velocity, pressureOf(end))
    curves.push({
      start,
      control1: c2,
      control2: c3,
      end,
      startWidth: lastWidth,
      endWidth: newWidth,
    })
    lastVelocity = velocity
    lastWidth = newWidth
    buffer.shift()
  })

  return {dots, curves}
}

function curvePoint(
  t: number,
  start: number,
  c1: number,
  c2: number,
  end: number,
): number {
  const u = 1 - t
  return (
    u * u * u * start +
    3 * u * u * t * c1 +
    3 * u * t * t * c2 +
    t * t * t * end
  )
}

function drawCurve(ctx: CanvasRenderingContext2D, curve: InkCurve) {
  let length = 0
  let px = curve.start.x
  let py = curve.start.y
  for (let i = 1; i <= 10; i += 1) {
    const t = i / 10
    const x = curvePoint(
      t,
      curve.start.x,
      curve.control1.x,
      curve.control2.x,
      curve.end.x,
    )
    const y = curvePoint(
      t,
      curve.start.y,
      curve.control1.y,
      curve.control2.y,
      curve.end.y,
    )
    length += Math.hypot(x - px, y - py)
    px = x
    py = y
  }
  const drawSteps = Math.max(1, Math.floor(length))
  const widthDelta = curve.endWidth - curve.startWidth
  ctx.beginPath()
  for (let i = 0; i < drawSteps; i += 1) {
    const t = i / drawSteps
    const ttt = t * t * t
    const x = curvePoint(
      t,
      curve.start.x,
      curve.control1.x,
      curve.control2.x,
      curve.end.x,
    )
    const y = curvePoint(
      t,
      curve.start.y,
      curve.control1.y,
      curve.control2.y,
      curve.end.y,
    )
    const width = Math.max(INK_MIN_WIDTH, curve.startWidth + ttt * widthDelta)
    ctx.moveTo(x, y)
    ctx.arc(x, y, width, 0, Math.PI * 2)
  }
  ctx.fill()
}

export function renderSignature(
  ctx: CanvasRenderingContext2D,
  cssWidth: number,
  cssHeight: number,
  strokes: InkPoint[][],
) {
  const canvas = ctx.canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (cssWidth <= 0 || cssHeight <= 0) return
  ctx.save()
  ctx.scale(canvas.width / cssWidth, canvas.height / cssHeight)
  ctx.fillStyle = '#000000'
  strokes.forEach(stroke => {
    const geometry = buildStrokeGeometry(stroke)
    geometry.dots.forEach(dot => {
      ctx.beginPath()
      ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2)
      ctx.fill()
    })
    geometry.curves.forEach(curve => drawCurve(ctx, curve))
  })
  ctx.restore()
}
